import React from 'react';
import { Card } from '../components/ui/card';
import { ExternalLink, Heart, Users, BookOpen, Stethoscope, AlertCircle } from 'lucide-react';

export const ResourcesPage = () => {
  const resources = {
    crisis: [
      {
        name: "National Suicide Prevention Lifeline",
        description: "24/7 crisis support",
        link: "tel:988",
        display: "Call or text 988",
        region: "US"
      },
      {
        name: "Crisis Text Line",
        description: "Text-based crisis support",
        link: "sms:741741",
        display: "Text HOME to 741741",
        region: "US, UK, Canada"
      },
      {
        name: "Samaritans",
        description: "24/7 emotional support",
        link: "tel:116123",
        display: "Call 116 123",
        region: "UK & Ireland"
      },
      {
        name: "Kids Help Phone",
        description: "Support for young people and families",
        link: "tel:1-800-668-6868",
        display: "1-800-668-6868",
        region: "Canada"
      }
    ],
    organizations: [
      {
        name: "PANDAS Network",
        description: "Leading advocacy and education organization for PANDAS/PANS",
        link: "https://pandasnetwork.org",
        display: "pandasnetwork.org",
        region: "International"
      },
      {
        name: "PANDAS Physicians Network",
        description: "Directory of PANDAS/PANS treating physicians",
        link: "https://pandasppn.org",
        display: "pandasppn.org",
        region: "International"
      },
      {
        name: "International OCD Foundation",
        description: "Resources for OCD and related disorders including PANDAS/PANS",
        link: "https://iocdf.org/about-pandas",
        display: "iocdf.org",
        region: "International"
      },
      {
        name: "Pediatric Autoimmune Neuropsychiatric Disorder Association (PANDA)",
        description: "UK-based support organization",
        link: "https://pandasuk.com",
        display: "pandasuk.com",
        region: "UK"
      },
      {
        name: "PANDAS/PANS Awareness",
        description: "Australian support and advocacy",
        link: "https://pandaspans.org.au",
        display: "pandaspans.org.au",
        region: "Australia"
      }
    ],
    support: [
      {
        name: "PANDAS Network Parent Support Groups",
        description: "Local and online support groups for parents",
        link: "https://pandasnetwork.org/support-groups",
        display: "Find a support group",
        region: "US & International"
      },
      {
        name: "PANDAS/PANS Facebook Support Groups",
        description: "Active parent communities (search for groups specific to your region)",
        link: "https://facebook.com",
        display: "facebook.com",
        region: "International"
      },
      {
        name: "ASPIRE - PANDAS Support",
        description: "UK parent support network",
        link: "https://aspire-pandas.org",
        display: "aspire-pandas.org",
        region: "UK"
      },
      {
        name: "PsychEd Connectory",
        description: "Connect with other families navigating similar challenges",
        link: "https://psychedconnectory.com",
        display: "psychedconnectory.com",
        region: "International"
      }
    ],
    research: [
      {
        name: "National Institute of Mental Health (NIMH) - PANDAS Research",
        description: "Latest research and clinical trials information",
        link: "https://www.nimh.nih.gov/health/publications/pandas",
        display: "nimh.nih.gov",
        region: "US"
      },
      {
        name: "Stanford PANS Clinic & Research",
        description: "Leading research center for PANDAS/PANS",
        link: "https://med.stanford.edu/pans.html",
        display: "Stanford Medicine",
        region: "US"
      },
      {
        name: "Yale Child Study Center",
        description: "Research and clinical expertise in PANDAS/PANS",
        link: "https://medicine.yale.edu/childstudy",
        display: "Yale Medicine",
        region: "US"
      },
      {
        name: "Journal of Child and Adolescent Psychopharmacology - PANS Guidelines",
        description: "Clinical treatment guidelines for PANS/PANDAS",
        link: "https://www.liebertpub.com/doi/10.1089/cap.2015.0084",
        display: "Treatment Guidelines",
        region: "International"
      },
      {
        name: "PubMed - PANDAS/PANS Research",
        description: "Search scientific literature",
        link: "https://pubmed.ncbi.nlm.nih.gov/?term=PANDAS+OR+PANS",
        display: "PubMed Database",
        region: "International"
      }
    ]
  };

  const sections = [
    { key: 'crisis', title: 'Crisis Resources', icon: AlertCircle, color: 'text-accent', bgColor: 'bg-accent/10' },
    { key: 'organizations', title: 'PANDAS/PANS Organizations', icon: Heart, color: 'text-primary', bgColor: 'bg-primary/10' },
    { key: 'support', title: 'Support & Community', icon: Users, color: 'text-secondary-foreground', bgColor: 'bg-secondary' },
    { key: 'research', title: 'Research & Education', icon: BookOpen, color: 'text-primary', bgColor: 'bg-primary/10' }
  ];

  return (
    <div className="space-y-8" data-testid="resources-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <Stethoscope className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            External Resources
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          Carefully curated links to trusted organizations, support networks, and research institutions.
        </p>
      </div>

      {/* Important Note */}
      <Card className="bg-secondary/20 rounded-xl border border-secondary p-6">
        <p className="text-[#57534E] leading-relaxed">
          <span className="font-semibold text-secondary-foreground">Important:</span> These are external resources. 
          Calm Pathways does not control their content. Always verify information with your healthcare provider.
        </p>
      </Card>

      {/* Resource Sections */}
      {sections.map(({ key, title, icon: Icon, color, bgColor }) => (
        <div key={key} className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`p-3 ${bgColor} rounded-lg`}>
              <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D]">{title}</h2>
          </div>

          <div className="grid gap-4">
            {resources[key].map((resource, i) => (
              <Card
                key={i}
                className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">{resource.name}</h3>
                    <p className="text-[#57534E] text-sm">{resource.description}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 font-semibold text-sm flex items-center gap-2"
                        data-testid={`resource-link-${i}`}
                      >
                        {resource.display}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <span className="px-3 py-1 bg-stone-100 text-[#57534E] rounded-full text-xs font-medium">
                        {resource.region}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {/* Footer Note */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl border border-stone-100 p-6 text-center">
        <p className="text-[#57534E] leading-relaxed">
          Can't find what you're looking for? Reach out in the <span className="font-semibold text-primary">Community Forum</span> - 
          other parents often have recommendations for regional resources.
        </p>
      </Card>
    </div>
  );
};

export default ResourcesPage;
